import {
  getTimezoneOffset,
  ISolution,
  getBeginningOfTheDay,
  UserActivityCategoryEnum,
  Activity,
  IDatesRange,
} from '@mobi-components/global-solution';

export function getSolutionDays(dateRange: IDatesRange | null): Date[] {
  if (!dateRange) {
    return [];
  }
  const {startDate, endDate} = dateRange;
  const dates = [];
  let date = startDate;
  while (date.valueOf() <= endDate.valueOf()) {
    dates.push(date);
    date = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  }
  return dates;
}

export function getListOfSolutionDays(solution?: ISolution | null, context?: string) {
  const allActivities = solution?.activities || [];
  // @ts-ignore
  const activities = context ? allActivities.filter((x) => x.spec?.context === context) : allActivities;
  let firstDate = activities.length ? new Date(activities[0].scheduled_start_time * 1000) : new Date();
  let lastDate = activities.length ? new Date(activities.slice(-1)[0].scheduled_start_time * 1000) : new Date();
  if (firstDate > lastDate) {
    const newDate = firstDate;
    firstDate = lastDate;
    lastDate = newDate;
  }

  return getSolutionDays({
    startDate: getBeginningOfTheDay(firstDate),
    endDate: getBeginningOfTheDay(lastDate),
  });
}

export function groupFilterSections(sourse: FilterTag[], category: UserActivityCategoryEnum): FilterSection[] {
  const groups = sourse.reduce((result: Record<string, number>, value: FilterTag) => {
    if (!value.parent) {
      return result;
    }
    result[value.parent] = result[value.parent] || 0;
    result[value.parent]++;
    return result;
  }, {});

  return Object.keys(groups)
    .filter((x) => groups[x] > 0)
    .map((x) => {
      return {
        id: x,
        name: x,
        options: sourse.filter((y) => y.domain === category && y.parent === x && y.name),
      };
    })
    .filter((x) => x.options.length > 0);
}

export function getDateInTimezone(localDate: Date, timezone: string) {
  const myOffset = new Date().getTimezoneOffset();
  const timezoneOffset = Math.round(getTimezoneOffset(localDate, timezone));

  return new Date(localDate.valueOf() - (timezoneOffset - myOffset) * 60 * 1000);
}

export function formatDate(localDate: Date) {
  const formatter = new Intl.DateTimeFormat(lang(), {weekday: 'long', year: 'numeric', month: 'short', day: 'numeric'});
  return formatter.format(localDate);
}

export function getDuration(durationMs: number) {
  const hours = Math.floor(durationMs / (3600 * 1000));
  const minutes = Math.floor((durationMs % (3600 * 1000)) / (60 * 1000));
  const fixedMinutes = minutes.toString().length > 1 ? `${minutes.toString()}m` : `${minutes.toString()}m`;
  const fixedHours = hours > 0 ? `${hours}h ` : '';
  return `${fixedHours}${fixedMinutes}`;
}

export function isFlightOverdue(flights: readonly Activity[]) {
  if (!flights) {
    return false;
  }

  const notValidDates = flights.some((x) => {
    // @ts-ignore
    return x.source?.content?.status === 'DATES_NOT_VALID';
  });
  return notValidDates;
}

export function isFlightsAvailable(flights: readonly Activity[]) {
  if (!flights) {
    return false;
  }

  if (isFlightOverdue(flights)) {
    return true;
  }

  return !flights.some((x) => {
    // @ts-ignore
    return x.source?.content?.status === 'NOT_AVAILABLE';
  });
}

export const waitForAllSelectorsAvailable = (selectors: string[]) => {
  let handler = 0;
  return new Promise((resolve) => {
    handler = setInterval(() => {
      const isAllAvailable = selectors.map((x) => document.querySelector(x)).every((x) => x);
      if (isAllAvailable) {
        clearInterval(handler);
        resolve(true);
      }
    }, 1000);
  });
};

export function lang() {
  return navigator.language;
}

export function formatBudget(value: number | string, currency = 'USD', suffix = '', maximumFractionDigits = 0) {
  let defaultValue = '';
  if (!value) {
    return defaultValue;
  }
  let formatter = new Intl.NumberFormat(lang(), {maximumFractionDigits});
  try {
    const options =
      currency === 'AMEX_POINT' ? {maximumFractionDigits} : {style: 'currency', currency, maximumFractionDigits};
    formatter = new Intl.NumberFormat(lang(), options);
  } catch {
    //
  }
  const v = parseFloat(value.toString());
  if (v > 0) {
    defaultValue = formatter.format(v);
  }
  return `${defaultValue} ${currency === 'AMEX_POINT' && defaultValue ? suffix : ''}`.trim();
}

export function formatBudgetValue(value: number | string, currency = 'USD') {
  const defaultValue = 'Budget';
  const result = formatBudget(value, currency);
  return result || defaultValue;
}
export interface Price {
  amount: number;
  currency: string;
}

export function formatPrices(prices: Price[], pointsName = '', maximumFractionDigits = 0) {
  const hash = prices.reduce((h: Record<string, number>, x) => {
    h[x.currency] = h[x.currency] ? h[x.currency] + x.amount : x.amount;
    return h;
  }, {});
  const hashValue = Object.keys(hash).reduce((r: Record<string, string>, x) => {
    r[x] = formatBudget(hash[x], x, pointsName, maximumFractionDigits);
    return r;
  }, {});
  const combined = Object.keys(hash).map((key) => {
    return {
      amount: hash[key],
      currency: key,
    };
  });
  return {
    prices: combined,
    values: {
      ...hash,
    },
    labels: {
      ...hashValue,
    },
  };
}

export enum DomainSection {
  PLACES = 'Places',
  TYPES = 'Types',
  NONE = '',
}

export interface FilterTag {
  id: string;
  domain: string;
  domainSection: DomainSection;
  parent: string;
  name: string;
  value: boolean;
}

export interface FilterSection {
  id: string;
  name: string;
  options: FilterTag[];
}

export function truncate(str: string, n: number, useWordBoundary: boolean) {
  if (str.length <= n) {
    return str;
  }
  const subString = str.substr(0, n - 1); // the original check
  return (useWordBoundary ? subString.substr(0, subString.lastIndexOf(' ')) : subString) + '...';
}

export function pluralFormatter(num: number, single: string, plural: string) {
  return num > 1 ? plural : single;
}

export enum MonthFormat {
  NUMERIC = 'numeric',
  TWO_DIGITS = '2-digit',
  LONG = 'long',
  SHORT = 'short',
  NARROW = 'narrow',
}
export function months(format: MonthFormat = MonthFormat.LONG) {
  const formatter = new Intl.DateTimeFormat(lang(), {month: format});
  return new Array(12).fill(0).map((_, i) => formatter.format(new Date(1970, i, 1)));
}

export interface TimeFilterValue {
  from?: number | null;
  to?: number | null;
}

export const timeFrameDefaultValue: Readonly<TimeFilterValue> = {
  from: null,
  to: null,
};

export function getReadableDuration(startDate: Date, endDate: Date): string {
  if (!startDate || !endDate) {
    return '';
  }
  const dates = {
    startDate,
    endDate,
  };
  const sameMonth = dates.startDate.getMonth() === dates.endDate.getMonth();
  const sameYear = dates.startDate.getFullYear() === dates.endDate.getFullYear();

  if (dates.startDate.valueOf() === dates.endDate.valueOf()) {
    return `${months()[dates.startDate.getMonth()]} ${dates.startDate.getDate()}, ${dates.startDate.getFullYear()}`;
  }

  if (sameMonth && sameYear) {
    return `${
      months()[dates.startDate.getMonth()]
    } ${dates.startDate.getDate()} - ${dates.endDate.getDate()}, ${dates.startDate.getFullYear()}`;
  }
  if (sameYear) {
    return `${months()[dates.startDate.getMonth()]} ${dates.startDate.getDate()} - ${
      months()[dates.endDate.getMonth()]
    } ${dates.endDate.getDate()}, ${dates.startDate.getFullYear()}`;
  }

  return `${months()[dates.startDate.getMonth()]} ${dates.startDate.getDate()}, ${dates.startDate.getFullYear()} - ${
    months()[dates.endDate.getMonth()]
  } ${dates.endDate.getDate()}, ${dates.endDate.getFullYear()}`;
}

export function formatTimeFrameValue(commonTimeFrame: TimeFilterValue = timeFrameDefaultValue) {
  const {from, to} = commonTimeFrame;
  if (!!from && !!to) {
    return getReadableDuration(new Date(from), new Date(to));
  }
  return 'Timeframe';
}
